import { Box, Button, Divider, FormControlLabel, Popover, Radio, RadioGroup, Stack, Typography, colors } from '@mui/material';
import { decode } from 'jsonwebtoken';
import React from 'react';
import { toast } from 'react-hot-toast';
import Cookies from 'universal-cookie';
import { isFunctionEligible } from '../helpers/eligibilityChecker';
import _ from 'lodash';
import { LoadingButton } from '@mui/lab';
import { fetchWith401Check } from '../helpers/http';
import PreviewIcon from '@mui/icons-material/ArrowForward';
import moment from 'moment/moment';
import DialogQuestions from './DialogQuestions';
const CommentStatus = ({ repository }) => {
  const [docStatus, setDocStatus] = React.useState('');
  const [trackChanges, setTrackChanges] = React.useState([]);
  const [loadingState, setLoadingState] = React.useState({ update: false });

  const [scores, setScores] = React.useState({
    contribution: 0,
    innovation: 0,
    clearness: 0,
  });

  const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;

  const reviewButtonRef = React.useRef();
  const [anchor, setAnchor] = React.useState(null);

  const cookies = new Cookies();
  const decoded = decode(cookies.get('token'));

  const [totalScores, setTotalScores] = React.useState(0);

  const ScoreCounter = ({ scores }) => {
    let score = parseInt(scores.innovation) + parseInt(scores.clearness) + parseInt(scores.contribution);

    const getColor = (score) => {
      if (score <= 8) return colors.red[700];
      if (score <= 10) return colors.amber[700];
      if (score <= 12) return colors.green[700];
    };
    return (
      <Typography variant='body2'>
        Total score: <span style={{ fontWeight: 'bold', color: getColor(score) }}>{score}</span>
      </Typography>
    );
  };

  const updateCurrentValues = () => {
    if (!repository?.Recommendations) return;
    const recommendations = repository?.Recommendations[0];
    if (recommendations?.status) {
      setDocStatus(recommendations?.status);
    }
    if (recommendations?.score_component) {
      setScores(recommendations?.score_component);
    }
    if (recommendations?.score) {
      setTotalScores(recommendations?.score);
    }
  };

  React.useEffect(() => {
    updateCurrentValues();
  }, [repository]);

  const changeRecommendationStatus = () => {
    if (totalScores >= 9) return setDocStatus('diterima');
    if (totalScores >= 6) return setDocStatus('membutuhkan_revisi');
    if (totalScores <= 5) return setDocStatus('ditolak');
  };

  React.useEffect(() => {
    changeRecommendationStatus();
  }, [totalScores]);

  const updateRecommendation = () => {
    if (!repository.id) return toast.error('No repository ID found');
    if (totalScores < 1) return toast.error('Please choose scoring first');
    // return alert(repId);
    const body = {
      repository_id: repository.id,
      status: docStatus,
      score: totalScores,
      score_component: scores,
      last_updated_by: decoded.username,
    };

    try {
      setLoadingState({ ...loadingState, update: true });
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify({
          ...body,
        }),
      };
      fetchWith401Check(`${API_URL}/recommendations`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          if (response?.status == 200) {
            setAnchor(null);
            return toast.success('Review updated successfully');
          }
        })
        .catch(function (err) {
          return toast.error(err.message);
        })
        .finally(function () {
          setLoadingState({ ...loadingState, update: false });
        });
    } catch (error) {
      toast.error(error.message);
      setLoadingState({ ...loadingState, update: false });
    }
  };

  React.useEffect(() => {
    let score = parseInt(scores.innovation) + parseInt(scores.clearness) + parseInt(scores.contribution);
    setTotalScores(score);
  }, [scores]);

  return (
    <Stack direction='column'>
      <Stack direction='column' spacing={1}>
        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
          Komponen Penilaian / <i>Scoring Component</i>
        </Typography>
        {isFunctionEligible(decoded.type, 'update_review_section') && (
          <Typography variant='caption'>
            Silakan memberikan nilai dengan menggunakan peringkat berikut: (1=sangat buruk; 2=buruk; 3= baik; 4= sangat baik) /
            <i>assessment components (please rate using the following ratings: 1= very good; 2=poor; 3=good; 4=excellent)</i>.
          </Typography>
        )}

        <Divider />

        <Stack direction='column'>
          <Typography variant='caption'>
            Kontribusi/Manfaat Terhadap Tujuan Pembangunan Berkelanjutan/<i>contribution / benefit to the Sustainable Development Goals</i>
          </Typography>
          <RadioGroup
            row
            aria-labelledby='contribution'
            value={scores.contribution}
            name='contribution'
            onChange={(e) => setScores({ ...scores, contribution: e.currentTarget.value })}
          >
            {[1, 2, 3, 4].map((item, index) => (
              <FormControlLabel
                key={item}
                value={item}
                control={<Radio />}
                label={
                  <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                    {item}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Stack>
        <Stack direction='column'>
          <Typography variant='caption'>
            Inovasi/<i>Innovation</i>
          </Typography>
          <RadioGroup
            row
            aria-labelledby='contribution'
            value={scores.innovation}
            name='innovation'
            onChange={(e) => setScores({ ...scores, innovation: e.currentTarget.value })}
          >
            {[1, 2, 3, 4].map((item, index) => (
              <FormControlLabel
                key={item}
                value={item}
                control={<Radio />}
                label={
                  <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                    {item}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Stack>
        <Stack direction='column'>
          <Typography variant='caption'>
            Kejelasan Narasi/Substansi / <i>clarity of narrative/substance</i>
          </Typography>
          <RadioGroup
            row
            aria-labelledby='clearness'
            value={scores.clearness}
            name='clearness'
            onChange={(e) => setScores({ ...scores, clearness: e.currentTarget.value })}
          >
            {[1, 2, 3, 4].map((item, index) => (
              <FormControlLabel
                key={item}
                value={item}
                control={<Radio />}
                label={
                  <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                    {item}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Stack>
      </Stack>
      <Stack direction='column' alignItems='flex-start' sx={{ width: '100%' }} spacing={2}>
        <Divider sx={{ width: '100%' }} />
        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
          Rekomendasi / <i>Recommendation</i>
        </Typography>
        {!_.isEmpty(repository?.Recommendations) && !_.isEmpty(repository?.Recommendations[0]) && repository?.Recommendations[0]?.last_updated_by && (
          <Stack direction='row'>
            <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
              Last reviewed by ({repository?.Recommendations[0]?.last_updated_by})
            </Typography>
          </Stack>
        )}
        <ScoreCounter scores={scores} />
        <RadioGroup row aria-labelledby='doc_status' value={docStatus} name='doc_status' onChange={(e) => setDocStatus(e.currentTarget.value)}>
          <FormControlLabel
            key='diterima'
            value='diterima'
            control={<Radio />}
            label={
              <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                Diterima / <i>Accept as it is</i>
              </Typography>
            }
          />
          <FormControlLabel
            key='membutuhkan_revisi'
            value='membutuhkan_revisi'
            control={<Radio />}
            label={
              <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                Membutuhkan Revisi / <i>Requires revision</i>
              </Typography>
            }
          />
          <FormControlLabel
            key='ditolak'
            value='ditolak'
            control={<Radio />}
            label={
              <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='caption'>
                Ditolak / <i>Reject</i>
              </Typography>
            }
          />
        </RadioGroup>
        {isFunctionEligible(decoded.type, 'update_review_section') && (
          <Stack direction='column' spacing={1}>
            <Box>
              <Button ref={reviewButtonRef} variant='contained' onClick={(e) => setAnchor(e.currentTarget)}>
                Update Review
              </Button>
            </Box>
            <Typography variant='caption'>This will send an email to the contributor of this repository</Typography>
          </Stack>
        )}
      </Stack>

      <Popover
        id='Popup'
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'top',
        }}
      >
        <Stack direction='column' sx={{ backgroundColor: colors.amber[500], minWidth: 400 }} p={1} justifyContent='center'>
          <Stack direction='column' sx={{ backgroundColor: 'white', minHeight: '100%' }} p={2} spacing={2}>
            <Stack direction='column' spacing={1}>
              <Typography variant='body2'>Are you sure want to update the review of this repository?</Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
              <Button sx={{ color: colors.grey[600] }} onClick={() => setAnchor(null)}>
                No
              </Button>
              <LoadingButton loading={loadingState.update} sx={{ color: colors.blue[400] }} onClick={updateRecommendation}>
                Yes
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default CommentStatus;
